import React, { useState } from 'react';
import { AlertCircle, Loader2, MapPin, ScanLine } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';
import { ScanTextInput } from '../../../common/inputs';
import { fetchLocationOverview } from './useLocationOverview';

interface LocationLookupSearchProps {
  warehouseId: string | null;
  onSelect: (binCode: string) => void;
  onBack?: () => void;
}

/**
 * Skann eller skriv en lokasjon. Skanning skjer kun i dette fokuserte feltet (ingen global scanner-hook),
 * som i varekortet. Enter slår opp lokasjonen i aktivt lager; finnes den ikke, blir operatøren stående her
 * med feltet tømt og fokusert, klar for neste skann.
 */
export const LocationLookupSearch: React.FC<LocationLookupSearchProps> = ({ warehouseId, onSelect, onBack }) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [notFoundTerm, setNotFoundTerm] = useState<string | null>(null);
  const [operatorBin, setOperatorBin] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const value = term.trim();
    if (!value || !warehouseId || loading) return;

    setLoading(true);
    setNotFoundTerm(null);
    setOperatorBin(null);
    setError(null);
    try {
      const result = await fetchLocationOverview(warehouseId, value);
      if (result?.isOperatorLocation) {
        setOperatorBin(result.bin.binCode);
        setTerm('');
      } else if (result) {
        onSelect(result.bin.binCode);
      } else {
        setNotFoundTerm(value);
        setTerm('');
      }
    } catch (err) {
      console.error('Error looking up location:', err);
      setError(t('warehouse.modules.locationLookup.search.error', {}, 'Kunne ikke slå opp lokasjonen'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          {onBack && <BackButton onClick={onBack} />}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {t('warehouse.modules.locationLookup.title', {}, 'Lokasjonsoppslag')}
              </h1>
            </div>
            <p className="text-sm text-gray-600">
              {t('warehouse.modules.locationLookup.description', {}, 'Skann eller skriv en lokasjon for å se hva som ligger der')}
            </p>
          </div>
        </div>

        <div className="relative">
          <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          <ScanTextInput
            value={term}
            onChange={(e) => { setTerm(e.target.value); setNotFoundTerm(null); setOperatorBin(null); setError(null); }}
            onKeyDown={handleKeyDown}
            placeholder={t('warehouse.modules.locationLookup.search.placeholder', {}, 'Skann eller skriv lokasjon...')}
            autoFocus
            disabled={!warehouseId}
            className="w-full pl-10 pr-10 py-3 min-h-[44px] text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-blue-500" />}
        </div>

        {!warehouseId && (
          <p className="mt-3 text-sm text-orange-600">
            {t('warehouse.modules.locationLookup.search.noWarehouse', {}, 'Velg lager i toppfeltet før du slår opp en lokasjon')}
          </p>
        )}
        {notFoundTerm && warehouseId && (
          <p className="mt-3 text-sm text-orange-600">
            {t('warehouse.modules.locationLookup.search.notFound', { '0': notFoundTerm, '1': warehouseId },
              `Fant ikke lokasjon «${notFoundTerm}» i lager ${warehouseId}`)}
          </p>
        )}
        {operatorBin && (
          <p className="mt-3 text-sm text-orange-600">
            {t('warehouse.modules.locationLookup.search.operatorLocation', { '0': operatorBin },
              `«${operatorBin}» er en operatørlokasjon og vises ikke i lokasjonsoppslaget`)}
          </p>
        )}
        {error && (
          <div className="mt-3 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};
